import React, { useState, useEffect } from 'react';
import { getRefiHistory, executeRefiAccounting, deleteRefi } from '../services/api';
import { format } from 'date-fns';

const RefiHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ days: 7, status: '', executionStatus: '' }); // Default to last 7 days, all statuses
  const [selectedRefi, setSelectedRefi] = useState(null);

  useEffect(() => {
    loadHistory();
  }, [filter]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const response = await getRefiHistory(filter);
      setHistory(response.data || []);
    } catch (error) {
      console.error('Error loading history:', error);
      alert('Failed to load refi history');
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async (refi) => {
    if (!refi.s3_summary_key) {
      alert('No S3 key found for this refi. Cannot execute.');
      return;
    }

    // Build new loan display (handle combined refis)
    const newLoanDisplay = getNewLoansDisplay(refi);
    const payoffDisplay = refi.is_combined_refi 
      ? `Combined Payoff: ${formatCurrency(refi.combined_payoff)}`
      : `Refi Payoff: ${formatCurrency(refi.refi_payoff)}`;

    const confirmMsg = `Execute refi accounting for ${refi.old_loan_number} → ${newLoanDisplay}?\n\n` +
                       `${payoffDisplay}\n\n` +
                       `This will apply all accounting changes to LoanPro.`;
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      console.log('🚀 Executing refi:', refi);
      const response = await executeRefiAccounting(refi.s3_summary_key);
      console.log('✅ Execution response:', response);
      
      alert(`✅ Refi execution triggered successfully!\n\nRequest ID: ${response.request_id}\n\nThe refi will be processed in the background. Refresh in a few seconds to see the updated status.`);
      
      // Reload history after 2 seconds
      setTimeout(() => loadHistory(), 2000);
    } catch (error) {
      console.error('❌ Error executing refi:', error);
      alert(`Failed to execute refi accounting:\n\n${error.response?.data?.error || error.message}`);
    }
  };

  const handleDelete = async (refi) => {
    if (!refi.s3_summary_key) {
      alert('No S3 key found for this refi. Cannot delete.');
      return;
    }

    const newLoanDisplay = getNewLoansDisplay(refi);
    const confirmMsg = `Delete this pending refi record?\n\n` +
                       `Old Loan: ${refi.old_loan_display_id || refi.old_loan_id}\n` +
                       `New Loan: ${newLoanDisplay}\n\n` +
                       `This removes the record from the dashboard. It does NOT reverse any changes in LoanPro.`;

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      console.log('🗑️ Deleting refi:', refi);
      await deleteRefi(refi.s3_summary_key);
      alert('✅ Refi record deleted successfully.');
      setTimeout(() => loadHistory(), 500);
    } catch (error) {
      console.error('❌ Error deleting refi:', error);
      alert(`Failed to delete refi record:\n\n${error.response?.data?.error || error.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending_approval: { class: 'badge badge-pending', text: '⏳ Pending Approval' },
      executed: { class: 'badge badge-success', text: '✅ Executed' },
      failed: { class: 'badge badge-error', text: '❌ Failed' },
      execution_failed: { class: 'badge badge-error', text: '❌ Execution Failed' },
    };
    const badge = statusMap[status] || { class: 'badge', text: status || 'N/A' };
    return <span className={badge.class}>{badge.text}</span>;
  };

  const getExecutionStatusBadge = (refi) => {
    const executionStatus = refi.execution_status;
    const status = refi.status;
    
    // Special case: if status is "failed", show that regardless of execution_status
    if (status === 'failed') {
      return <span className="badge badge-error">❌ Failed</span>;
    }
    
    // Special case: if status is "executed" but execution_status is "pending_approval",
    // this means it ran in production mode (not via manual approval workflow)
    if (status === 'executed' && executionStatus === 'pending_approval') {
      return <span className="badge badge-success">✅ Executed (Production)</span>;
    }
    
    const statusMap = {
      pending_approval: { class: 'badge badge-pending', text: '⏳ Awaiting Approval' },
      approved_executing: { class: 'badge badge-pending', text: '🔄 Executing...' },
      executed: { class: 'badge badge-success', text: '✅ Executed' },
      execution_failed: { class: 'badge badge-error', text: '❌ Execution Failed' },
    };
    
    const badge = statusMap[executionStatus] || { class: 'badge', text: executionStatus || 'N/A' };
    return <span className={badge.class}>{badge.text}</span>;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm:ss');
    } catch {
      return dateString;
    }
  };

  const isCombinedRefi = (refi) => {
    const loanCount = parseInt(refi.new_loan_count) || 0;
    return refi.is_combined_refi === true && loanCount > 1;
  };

  const getNewLoansDisplay = (refi) => {
    // Check if this is a combined refi with multiple new loans
    if (isCombinedRefi(refi) && refi.new_loans && Array.isArray(refi.new_loans) && refi.new_loans.length > 0) {
      return refi.new_loans.map(loan => loan.loan_number).join(', ');
    }
    // Single loan - use display ID or fall back to ID
    return refi.new_loan_display_id || refi.new_loan_id;
  };

  const getRefiPayoffDisplay = (refi) => {
    // For combined refis, show the combined payoff
    if (isCombinedRefi(refi) && refi.combined_payoff) {
      return formatCurrency(refi.combined_payoff);
    }
    // Single refi - show individual payoff
    return formatCurrency(refi.refi_payoff);
  };

  return (
    <div className="card">
      <div style={styles.header}>
        <h2>Refi Processing History</h2>
        
        {/* Filters */}
        <div style={styles.filters}>
          <label style={styles.filterLabel}>
            Days:
            <select
              value={filter.days}
              onChange={(e) => setFilter({ ...filter, days: parseInt(e.target.value) })}
              style={styles.select}
            >
              <option value={1}>Today</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </label>
          
          <label style={styles.filterLabel}>
            Status:
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value })}
              style={styles.select}
            >
              <option value="">All</option>
              <option value="pending_approval">⏳ Pending Approval</option>
              <option value="executed">✅ Executed</option>
              <option value="failed">❌ Failed (Preview)</option>
              <option value="execution_failed">❌ Execution Failed</option>
            </select>
          </label>
          
          <label style={styles.filterLabel}>
            Execution:
            <select
              value={filter.execution_status || ''}
              onChange={(e) => setFilter({ ...filter, execution_status: e.target.value || undefined })}
              style={styles.select}
            >
              <option value="">All</option>
              <option value="pending_approval">⏳ Awaiting Approval</option>
              <option value="executed">✅ Executed</option>
              <option value="approved_executing">🔄 Executing</option>
              <option value="execution_failed">❌ Failed</option>
            </select>
          </label>
          
          <button className="button button-secondary" onClick={loadHistory}>
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : history.length === 0 ? (
        <div style={styles.emptyState}>
          <p>No refi records found</p>
        </div>
      ) : (
        <>
          <div style={styles.tableContainer}>
            <table className="table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Old Loan</th>
                  <th>New Loan</th>
                  <th>Refi Payoff</th>
                  <th>Adjustment</th>
                  <th>Status</th>
                  <th>Execution</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((refi, index) => (
                  <tr key={index}>
                    <td>{formatDate(refi.timestamp || refi.processed_at)}</td>
                    <td>
                      {refi.old_loan_display_id || refi.old_loan_id}
                      {isCombinedRefi(refi) && (
                        <span className="combined-badge"> 🔀 Combined</span>
                      )}
                    </td>
                    <td>
                      {getNewLoansDisplay(refi)}
                      {isCombinedRefi(refi) && (
                        <div style={styles.loanCount}>({refi.new_loan_count} loans)</div>
                      )}
                    </td>
                    <td>{getRefiPayoffDisplay(refi)}</td>
                    <td>
                      <span style={styles.adjustmentType}>
                        {refi.adjustment_type || 'unknown'}
                      </span>
                      {' '}
                      {formatCurrency(refi.adjustment_amount || 0)}
                    </td>
                    <td>{getStatusBadge(refi.status)}</td>
                    <td>{getExecutionStatusBadge(refi)}</td>
                    <td>
                      <div style={styles.actionsCell}>
                        <button
                          className="button button-primary"
                          style={styles.viewButton}
                          onClick={() => setSelectedRefi(refi)}
                        >
                          View Details
                        </button>
                        {/* Execute: only for pending_approval entries not yet run in production */}
                        {refi.execution_status === 'pending_approval' && refi.status !== 'executed' && (
                          <button
                            className="button button-success"
                            style={styles.executeButton}
                            onClick={() => handleExecute(refi)}
                          >
                            🚀 Execute
                          </button>
                        )}
                        {/* Delete: available for any non-executed record */}
                        {refi.execution_status !== 'executed' && refi.status !== 'executed' && (
                          <button
                            className="button"
                            style={styles.deleteButton}
                            onClick={() => handleDelete(refi)}
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Details Modal */}
          {selectedRefi && (
            <div style={styles.modal} onClick={() => setSelectedRefi(null)}>
              <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                  <h3>Refi Details</h3>
                  <button
                    className="button button-secondary"
                    onClick={() => setSelectedRefi(null)}
                  >
                    Close
                  </button>
                </div>
                
                <div style={styles.modalBody}>
                  <>
                    <div style={styles.detailsGrid}>
                      <div style={styles.detailItem}>
                        <strong>Old Loan:</strong> {selectedRefi.old_loan_number || selectedRefi.old_loan_id}
                      </div>
                      {selectedRefi.is_combined_refi ? (
                        <>
                          <div style={styles.detailItem}>
                            <strong>Type:</strong> <span className="combined-badge">🔀 Combined Refi</span>
                          </div>
                          <div style={styles.detailItem}>
                            <strong>New Loan Count:</strong> {selectedRefi.new_loan_count || selectedRefi.new_loans?.length || 'N/A'}
                          </div>
                          <div style={styles.detailItem}>
                            <strong>Combined Payoff:</strong> {formatCurrency(selectedRefi.combined_payoff)}
                          </div>
                        </>
                      ) : (
                        <>
                          <div style={styles.detailItem}>
                            <strong>New Loan:</strong> {selectedRefi.new_loan_display_id || selectedRefi.new_loan_id}
                          </div>
                          <div style={styles.detailItem}>
                            <strong>Refi Payoff:</strong> {formatCurrency(selectedRefi.refi_payoff)}
                          </div>
                        </>
                      )}
                      <div style={styles.detailItem}>
                        <strong>Refi Party:</strong> {selectedRefi.refi_party || 'N/A'}
                      </div>
                      <div style={styles.detailItem}>
                        <strong>Adjustment Type:</strong> {selectedRefi.adjustment_type || 'N/A'}
                      </div>
                      <div style={styles.detailItem}>
                        <strong>Adjustment Amount:</strong> {formatCurrency(selectedRefi.adjustment_amount || 0)}
                      </div>
                      <div style={styles.detailItem}>
                        <strong>Status:</strong> {getStatusBadge(selectedRefi.status)}
                      </div>
                      <div style={styles.detailItem}>
                        <strong>Execution Status:</strong> {getExecutionStatusBadge(selectedRefi)}
                      </div>
                      <div style={styles.detailItem}>
                        <strong>Processed:</strong> {formatDate(selectedRefi.timestamp || selectedRefi.processed_at)}
                      </div>
                      {selectedRefi.executed_by && (
                        <div style={styles.detailItem}>
                          <strong>Executed By:</strong> {selectedRefi.executed_by}
                        </div>
                      )}
                      {selectedRefi.executed_at && (
                        <div style={styles.detailItem}>
                          <strong>Executed At:</strong> {formatDate(selectedRefi.executed_at)}
                        </div>
                      )}
                    </div>
                    
                    {/* Show breakdown of new loans for combined refis */}
                    {selectedRefi.is_combined_refi && selectedRefi.new_loans && selectedRefi.new_loans.length > 0 && (
                      <div style={styles.newLoansSection}>
                        <h4>New Loans Breakdown:</h4>
                        <table className="table" style={styles.nestedTable}>
                          <thead>
                            <tr>
                              <th>Loan Number</th>
                              <th>Refi Payoff</th>
                              <th>Prorated Adjustment</th>
                              <th>Processed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedRefi.new_loans.map((loan, idx) => (
                              <tr key={idx}>
                                <td>{loan.loan_number || loan.loan_id}</td>
                                <td>{formatCurrency(loan.refi_payoff)}</td>
                                <td>
                                  {loan.prorated_adjustment ? (
                                    <span>
                                      <span style={{textTransform: 'capitalize', fontWeight: '600'}}>
                                        {loan.prorated_adjustment_type || selectedRefi.adjustment_type}
                                      </span>
                                      {' '}
                                      {formatCurrency(Math.abs(parseFloat(loan.prorated_adjustment)))}
                                    </span>
                                  ) : 'N/A'}
                                </td>
                                <td>{formatDate(loan.timestamp)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    
                    {selectedRefi.steps_completed && selectedRefi.steps_completed.length > 0 && (
                      <div className="section-success" style={{ marginTop: '20px', padding: '15px' }}>
                        <h4>Steps Completed:</h4>
                        <ul>
                          {selectedRefi.steps_completed.map((step, idx) => (
                            <li key={idx}>{step.action || step}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {selectedRefi.error && (
                      <div className="section-error" style={{ marginTop: '20px', padding: '15px' }}>
                        <h4>Error:</h4>
                        <pre style={styles.errorPre}>{selectedRefi.error}</pre>
                      </div>
                    )}
                  </>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  header: {
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '15px',
  },
  filters: {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  filterLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
    color: 'var(--text-primary)',
  },
  select: {
    padding: '8px 12px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: 'var(--input-bg)',
    color: 'var(--text-primary)',
  },
  tableContainer: {
    overflowX: 'auto',
  },
  adjustmentType: {
    fontWeight: '600',
    color: 'var(--text-muted)',
  },
  actionsCell: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  viewButton: {
    padding: '6px 12px',
    fontSize: '12px',
  },
  executeButton: {
    padding: '6px 12px',
    fontSize: '12px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  deleteButton: {
    padding: '6px 12px',
    fontSize: '12px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '600',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: 'var(--text-muted)',
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'var(--modal-overlay)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'var(--modal-bg)',
    borderRadius: '8px',
    maxWidth: '800px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },
  modalHeader: {
    padding: '20px',
    borderBottom: '1px solid var(--border-color)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalBody: {
    padding: '20px',
  },
  detailsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px',
    marginBottom: '20px',
  },
  detailItem: {
    padding: '10px',
    backgroundColor: 'var(--card-bg-hover)',
    borderRadius: '4px',
    color: 'var(--text-primary)',
  },
  errorPre: {
    whiteSpace: 'pre-wrap',
    fontSize: '12px',
    color: '#f85149',
    maxHeight: '200px',
    overflow: 'auto',
  },
  loanCount: {
    fontSize: '11px',
    color: 'var(--text-muted)',
    marginTop: '4px',
  },
  newLoansSection: {
    marginTop: '20px',
    padding: '15px',
    backgroundColor: 'var(--card-bg-hover)',
    borderRadius: '4px',
  },
  nestedTable: {
    marginTop: '10px',
    fontSize: '13px',
  },
};

export default RefiHistory;

